"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { fetchMatchOdds } from "@/app/(app)/actions/fetchMatchOdds";
import { useLocale } from "@/lib/labels/LabelsProvider";
import type { OddsData, BookmakerOdds, MarketOddsMap } from "./types";

/** Default polling interval in milliseconds (30 seconds) */
export const DEFAULT_ODDS_POLLING_INTERVAL_MS = 30000;

interface UseMatchOddsOptions {
    matchId: string;
    operatorId?: string;
    /**
     * Restrict the fetch to specific markets. Omit for the API default
     * (typically 1x2 only). Accepts both the SDK's mapped keys (`"1x2"`,
     * `"OVER_UNDER"`, …) and raw API codes (`"OVER_GOALS_1_5"`, …).
     */
    markets?: string[];
    /** Whether the match is live — enables polling when `enablePolling` is unset. */
    isLive?: boolean;
    /** Polling interval in milliseconds for live matches (default: 30000ms / 30 seconds) */
    pollingIntervalMs?: number;
    /** Whether to enable polling (default: true when isLive is true) */
    enablePolling?: boolean;
}

/**
 * Custom hook to fetch betting odds for a single match.
 *
 * The returned `oddsMap` is keyed by the **raw API market code**
 * (`FT_1X2`, `HT_1X2`, `OVER_GOALS_2_5`, `ASIAN_HANDICAP_neg0_5`,
 * `OVER_CORNERS_9_5`, …). Consumers group by family (e.g. every
 * `OVER_GOALS_*`) themselves. This avoids the pre-refactor collapse
 * where `FT_1X2` and `HT_1X2` shared a bucket and every Over/Under
 * threshold merged under a single `OVER_UNDER` key that the UI then
 * had to re-split by parsing label text.
 */
export function useMatchOdds({
    matchId,
    operatorId,
    markets,
    isLive = false,
    pollingIntervalMs = DEFAULT_ODDS_POLLING_INTERVAL_MS,
    enablePolling,
}: UseMatchOddsOptions) {
    const locale = useLocale();
    // Stable dep key — new array identity per render shouldn't restart the effect.
    const marketsKey = markets ? [...markets].sort().join(",") : "";
    const [oddsMap, setOddsMap] = useState<MarketOddsMap>(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Track if this is the initial load vs a refresh
    const isInitialLoad = useRef(true);

    // Determine if polling should be enabled (default to true for live matches).
    // The by-match-id endpoint no longer takes a PRE_EVENT/LIVE toggle — it
    // returns whatever's current for the match — so polling is the only knob.
    const shouldPoll = enablePolling ?? isLive;

    const loadOdds = useCallback(
        async (isRefresh = false) => {
            if (!matchId) {
                setLoading(false);
                return;
            }

            try {
                // Only show loading on initial load, not on refreshes
                if (!isRefresh) {
                    setLoading(true);
                }
                setError(null);

                // Fetch odds using server action. When `markets` is omitted
                // the API returns its default (typically 1x2 only) — callers
                // that need multiple markets must list them explicitly.
                const odds = await fetchMatchOdds({
                    matchId,
                    marketTypes: markets,
                    locale,
                });

                const newOddsMap: MarketOddsMap = new Map();

                if (odds && odds.operators && odds.operators.length > 0) {
                    // Filter by operatorId if provided
                    const operators = operatorId
                        ? odds.operators.filter((op) => op.operator.id === operatorId)
                        : odds.operators;

                    // Process each operator's markets
                    operators.forEach((operatorOdds) => {
                        const operator = operatorOdds.operator;

                        operatorOdds.markets.forEach((market) => {
                            // Key by raw API code so different lines / halves
                            // stay in their own buckets. UI groups by family.
                            const marketCode = market.type.code;

                            // Map selections to our OddsData format.
                            // Label is the raw selection code (e.g. "over", "1", "x", "1x"); the
                            // OddsTab wraps it with `translateOddsSelections` per market family so
                            // the UI reads "Over" / "1" / "X" / "1X" localised. We deliberately do
                            // NOT concatenate `selection.value` here (which for over/under carries
                            // the threshold, "Over 2.5") because the card title already shows the
                            // line and the button-level label should stay short.
                            const selections: OddsData[] = market.selections.map((selection) => {
                                let change: "up" | "down" | "none" = "none";
                                if (selection.movement === "UP") change = "up";
                                else if (selection.movement === "DOWN") change = "down";

                                return {
                                    id: selection.id,
                                    providerSelectionId: selection.providerSelectionId,
                                    label: selection.name,
                                    value: parseFloat(selection.odds).toFixed(2),
                                    url: selection.url,
                                    change,
                                };
                            });

                            const bookmakerOdds: BookmakerOdds = {
                                odds: selections,
                                operator: {
                                    name: operator.name,
                                    url: operator.url || "",
                                    ...(operator.branding?.logo && {
                                        logo: {
                                            url: operator.branding.logo,
                                            alt: `${operator.name} logo`,
                                        },
                                    }),
                                    backgroundColor: operator.branding?.backgroundColor || "#000000",
                                    textColor: operator.branding?.textColor || "#ffffff",
                                },
                            };

                            // Add to existing market array or create new one
                            const existing = newOddsMap.get(marketCode) || [];
                            existing.push(bookmakerOdds);
                            newOddsMap.set(marketCode, existing);
                        });
                    });
                }

                setOddsMap(newOddsMap);
            } catch (err) {
                console.error("Error fetching match odds:", err);
                setError(err instanceof Error ? err : new Error("Failed to fetch odds"));
            } finally {
                setLoading(false);
                isInitialLoad.current = false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [matchId, operatorId, marketsKey, locale]
    );

    // Initial load
    useEffect(() => {
        isInitialLoad.current = true;
        loadOdds(false);
    }, [loadOdds]);

    // Set up polling for live matches
    useEffect(() => {
        if (!shouldPoll || !matchId) {
            return;
        }

        const intervalId = setInterval(() => {
            loadOdds(true);
        }, pollingIntervalMs);

        return () => {
            clearInterval(intervalId);
        };
    }, [shouldPoll, matchId, pollingIntervalMs, loadOdds]);

    // Manual refetch function
    const refetch = useCallback(() => {
        loadOdds(true);
    }, [loadOdds]);

    return { oddsMap, loading, error, refetch };
}
